
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePropertyDetail } from "@/hooks/usePropertyDetail";
import { useAuth } from "@/contexts/AuthContext";
import PublicHeader from "@/components/public/PublicHeader";
import { PropertyDetailContent } from "@/components/properties/details/PropertyDetailContent";
import { PropertyDetailSidebar } from "@/components/properties/details/PropertyDetailSidebar";
import { PropertyDetailSkeleton } from "@/components/properties/details/PropertyDetailSkeleton";
import { formatAddressForDisplay, getMetaRobots, createSafePropertyTitle, obfuscateStreetNumber } from "@/utils/addressPrivacy";
import { formatLocalDate } from "@/utils/dateUtils";
import { Helmet } from "react-helmet-async";
import { PropertyShareButtons } from "@/components/common/SocialShareButtons";

const PublicPropertyDetailPage = () => {
  const { propertyId, sellerId } = useParams<{ propertyId: string; sellerId: string }>();
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const { 
    isLoading, 
    property, 
    propertyImages, 
    sellerInfo, 
    daysOnSite,
    totalImageCount,
    hasMoreImages,
    loadMoreImages,
    isLoadingMoreImages
  } = usePropertyDetail(propertyId);

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return "N/A";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string | null | Date) =>
    formatLocalDate(dateString, { year: 'numeric', month: 'long', day: 'numeric' });

  const calculatedGrossMargin = property && property.arv_value && property.price 
    ? property.arv_value - property.price 
    : property?.gross_margin || null;

  // Determine display address based on authentication
  // Unauthenticated: Remove street number (e.g., "Cherrylawn St" instead of "17366 Cherrylawn St")
  // Authenticated: Show full address
  const displayAddress = property 
    ? (isAuthenticated ? property.address : obfuscateStreetNumber(property.address))
    : '';

  const pageTitle = property ? createSafePropertyTitle(
    {
      address: property.address,
      city: property.city,
      state: property.state,
      zip_code: property.zip_code
    },
    isAuthenticated
  ) : 'Property Details';

  const robotsDirective = getMetaRobots(isAuthenticated && !!property);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="container mx-auto px-4 py-8">
          <PropertyDetailSkeleton />
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center py-16">
          <div className="text-center max-w-md mx-auto px-4">
            <h1 className="text-2xl font-bold mb-4">This Property is No Longer Available</h1>
            <p className="text-muted-foreground mb-6">
              This property may have been removed from the marketplace or the listing has expired.
            </p>
            <div className="space-y-4">
              {sellerId && (
                <Link to={`/public/seller/${sellerId}/listings`}>
                  <Button variant="outline" className="w-full">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Seller's Listings
                  </Button>
                </Link>
              )}
              <a 
                href="/buyer/register" 
                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors w-full"
              >
                Sign Up to Browse Properties
              </a>
              <p className="text-xs text-muted-foreground">
                Free for buyers • Discover investment opportunities
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="robots" content={robotsDirective} />
        {!isAuthenticated && property && (
          <>
            <meta name="description" content={`Investment property opportunity in ${property.city}, ${property.state}. Sign up to view full details including address and investment metrics.`} />
            <link rel="canonical" href={window.location.href} />
          </>
        )}
      </Helmet>
      <PublicHeader />
      <div className="container mx-auto px-4 py-8">
        {/* Back to listings button */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <Link to={`/public/seller/${sellerId}/listings`}>
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Listings
            </Button>
          </Link>
          {sellerInfo?.id && (
            <PropertyShareButtons
              propertyId={property.id}
              sellerId={sellerInfo.id}
              propertyAddress={property.address}
            />
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <PropertyDetailContent
            property={property}
            propertyImages={propertyImages}
            daysOnSite={daysOnSite}
            calculatedGrossMargin={calculatedGrossMargin}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            hideContractExpiration={true}
            displayAddress={displayAddress}
            totalImageCount={totalImageCount}
            hasMoreImages={hasMoreImages}
            onLoadMoreImages={loadMoreImages}
            isLoadingMoreImages={isLoadingMoreImages}
            showViewCount={false}
            isAuthenticated={isAuthenticated}
            hideGrossMargin={!!sellerInfo?.hide_gross_margin}
          />
          
          <PropertyDetailSidebar
            sellerInfo={sellerInfo}
            address={displayAddress}
            city={property.city}
            state={property.state}
            zipCode={property.zip_code}
            zillowLink={property.zillow_link}
            propertyId={property.id}
            price={property.price}
            bedrooms={property.bedrooms}
            bathrooms={property.bathrooms}
            propertyType={property.property_type}
            isAuthenticated={isAuthenticated}
          />
        </div>
      </div>
    </div>
  );
};

export default PublicPropertyDetailPage;
